import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import socketIOClient from 'socket.io-client';
import { useIsOnline } from 'react-use-is-online';
import { WifiOff } from 'lucide-react';
import store from 'store';

import { IconButton } from '@/components/ui/custom/IconButton';

import {
	getCustomerConversations,
	syncCustomerConversationsAfterIdle,
	updateConversationsNotes,
	updateConversationFolder,
	addConversationToCustomerConversations,
	addMessageToCustomerConversation,
	setWebchatSocketId,
} from '@/actions/webchat';

import {
	addTypingIndicator,
	removeTypingIndicator,
	clearTypingIndicatorForMessage,
} from '@/actions/typingIndicator';

import { getAllFolders } from '@/actions/folders';
import { fetchCurrentVoiceStatus } from '@/actions/voice';
import {
	setForwardingStatus,
	setForwardingTargetUser,
} from '@/reducers/voiceReducer';

import { setBusinessGroup } from '@/actions/businessGroup';
import {
	addTeamConversation,
	addMessageToConversation,
	removeTeamConversationFromState,
	updateTeamMessage,
	catchUpTeamConversations,
} from '@/actions/teams';

import currentLocationNotification from '../../webchat/assets/notification.mp3';
import otherLocationNotification from '../../webchat/assets/other-location-notification.mp3';

import { setUserOnline, setUserOffline } from '../../../actions/users';

import getRealtimeApiUrl from '../../../utils/getRealtimeApiUrl';

import { toast } from 'sonner';
import { util as workflowEventUtil } from '@/slices/workflowEventSlice';
import { util as projectUtil } from '@/slices/projectSlice';
import {
	setAiStatus,
	setAiDraft,
	setAiEscalation,
	fetchPendingEscalations,
	clearEscalationIfClosed,
} from '@/slices/aiEmployeeSlice';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import { createAiStatusListener } from '@/utils/aiStatusEvents';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import { toastPosition } from '@/components/notifications/toastPosition';

const SOCKET_REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000;
// socket.io reconnects on its own (backoff caps around 7.5s), so only warn
// once the connection has been down longer than a normal reconnect takes.
const DISCONNECTED_GRACE_MS = 10000;

const playNotificationAudio = (audio) => {
	const playPromise = audio?.play?.();
	if (playPromise && typeof playPromise.catch === 'function') {
		playPromise.catch(() => {
			// Autoplay is blocked until the user has interacted with the page.
		});
	}
};

// connectionChecker component handles the connection status of the webchat
const ConnectionChecker = () => {
	const { isOnline } = useIsOnline();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const currentLocationAudio = useRef(new Audio(currentLocationNotification));
	const otherLocationAudio = useRef(new Audio(otherLocationNotification));
	const webchatSocketRef = useRef(null); // Use useRef to hold the socket instance
	const businessGroupRef = useRef(null); // Track current businessGroup value
	const authRef = useRef(null); // Track current auth value for long-lived socket handlers
	const wasDisconnectedRef = useRef(false);
	const disconnectedAtRef = useRef(null);
	const lastCatchUpAtRef = useRef(0);
	const catchUpInFlightRef = useRef(false);
	const hiddenAtRef = useRef(null);
	const pathnameRef = useRef(pathname);
	// Outlives socket reconnects, so a pending "thinking" timeout is not lost
	// when the socket effect re-runs.
	const aiStatusListenerRef = useRef(null);
	if (!aiStatusListenerRef.current) {
		aiStatusListenerRef.current = createAiStatusListener((event) =>
			dispatch(setAiStatus(event))
		);
	}

	useEffect(() => () => aiStatusListenerRef.current?.dispose(), []);

	const [webchatSocketConnected, setWebchatSocketConnected] = useState(false);
	const [seconds, setSeconds] = useState(0);
	const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
	const [showDisconnected, setShowDisconnected] = useState(false);

	const auth = useSelector((state) => state.auth);
	const businessGroup = useSelector((state) => state.businessGroup);

	const showNewLayout = getShowNewLayout(auth);
	const catchUpThrottleMs = 120000;
	const authUser = auth?.user;
	const userId = authUser?._id;
	const temporaryBusiness = authUser?._temporaryBusiness;
	const socketIdentityKey = userId ? `${userId}:${temporaryBusiness || ''}` : '';
	const isEnterpriseEnabled = !!authUser?.isEnterpriseEnabled;
	const isTeamChatEnabled = !!auth?.products?.includes('teams');
	const aiEmployeeVisible = hasAiEmployeeProduct(auth);
	const businessCount = authUser?._businesses?.length || 0;

	const reset = useCallback(() => {
		setSeconds(0);
	}, []);

	const refresh = useCallback(() => {
		location.reload();
		return false;
	}, []);

	// Drops a group another member deleted and steps off its deep link so the
	// view is not left pointing at a conversation that no longer exists.
	const handleDeletedTeamConversation = useCallback(
		(_teamConversation) => {
			if (!_teamConversation) {
				return;
			}

			dispatch(removeTeamConversationFromState({ _teamConversation }));

			if (pathnameRef.current?.includes(_teamConversation)) {
				navigate('/groupchat/groups');
			}
		},
		[dispatch, navigate]
	);

	// BrowserRouter's useNavigate() changes identity on every pathname change.
	// Keep the handler in a ref so the socket effect does not reconnect (and
	// flash the "Connection lost" toast) on every conversation click.
	const handleDeletedTeamConversationRef = useRef(
		handleDeletedTeamConversation
	);

	useEffect(() => {
		handleDeletedTeamConversationRef.current =
			handleDeletedTeamConversation;
	}, [handleDeletedTeamConversation]);

	const runWebchatCatchUp = useCallback(async ({ force = false, changedSince } = {}) => {
		const now = Date.now();

		if (catchUpInFlightRef.current) {
			return;
		}

		if (!force && now - lastCatchUpAtRef.current < catchUpThrottleMs) {
			return;
		}

		catchUpInFlightRef.current = true;
		lastCatchUpAtRef.current = now;

		if (authRef.current?.products?.includes('teams')) {
			dispatch(catchUpTeamConversations());
		}

		try {
			const result = await dispatch(
				syncCustomerConversationsAfterIdle({ changedSince })
			);

			if (result?.logout) {
				refresh();
			}
		} finally {
			catchUpInFlightRef.current = false;
		}
	}, [dispatch, refresh]);

	const playNotificationSound = useCallback((
		incomingConversation = null,
		otherLocation = false
	) => {
		const shouldPlaySound = store.get('playing-notification-sound', true);

		if (!shouldPlaySound) {
			return;
		}

		if (!incomingConversation) {
			return playNotificationAudio(
				otherLocation
					? otherLocationAudio.current
					: currentLocationAudio.current
			);
		}

		// do not play sound if the conversation is assigned to someone else
		// _assignees (array) is primary, _assignee (single) is deprecated fallback
		const isAssignedToOther = () => {
			const currentUserId = authRef.current?.user?._id;

			if (incomingConversation._assignees?.length > 0) {
				return !incomingConversation._assignees.includes(currentUserId);
			}
			// Fallback to deprecated _assignee field
			return (
				incomingConversation._assignee &&
				incomingConversation._assignee !== currentUserId
			);
		};

		if (incomingConversation && isAssignedToOther()) {
			console.log('assigned to someone else, no sound');
			return;
		}

		const lastMessage =
			incomingConversation.messages[
				incomingConversation.messages.length - 1
			];

		// first check if the last message was a customer message
		if (lastMessage && lastMessage.sender === 'customer') {
			// if so, play the sound and exit
			return playNotificationAudio(
				otherLocation
					? otherLocationAudio.current
					: currentLocationAudio.current
			);
		}

		// if the last message was a event, do not play a sound
		if (lastMessage && lastMessage.sender === 'event') {
			return;
		}

		// if the last message was a business or AI message, do not play a sound
		if (lastMessage && (lastMessage.sender === 'business' || lastMessage.sender === 'ai')) {
			return;
		}

		// otherwise check to see if the message was a textchat message (the last two messages send within 2 seconds of one another)
		const secondLastMessage =
			incomingConversation.messages[
				incomingConversation.messages.length - 2
			];

		if (
			!lastMessage ||
			!secondLastMessage ||
			!lastMessage.createdAt ||
			!secondLastMessage.createdAt
		) {
			// error, back out
			return;
		}

		const lastMessageDate = new Date(lastMessage.createdAt);

		const secondLastMessageDate = new Date(secondLastMessage.createdAt);

		const timeBetweenTwoDates =
			(secondLastMessageDate - lastMessageDate) / 1000;

		const isLastMessageAndSecondLastMessageImmediatelyProceedEachOther =
			timeBetweenTwoDates < 2; // two messages are within 2 seconds of each other (e.g. a customer sent a message and chekkit bot replied), play notification

		if (isLastMessageAndSecondLastMessageImmediatelyProceedEachOther) {
			return playNotificationAudio(
				otherLocation
					? otherLocationAudio.current
					: currentLocationAudio.current
			);
		}
	}, []);

	// Update the ref whenever businessGroup changes
	useEffect(() => {
		businessGroupRef.current = businessGroup;
	}, [businessGroup]);

	useEffect(() => {
		authRef.current = auth;
	}, [auth]);

	// Socket handlers are registered once, so the current path is read from a ref
	useEffect(() => {
		pathnameRef.current = pathname;
	}, [pathname]);

	// Fetch initial voice status when component mounts
	useEffect(() => {
		dispatch(fetchCurrentVoiceStatus());
	}, [dispatch]);

	// navigator.onLine can report offline while the network works (VPNs, sleep,
	// network switches), so only the socket decides whether to warn.
	const isConnected = webchatSocketConnected;

	// Hidden tabs are skipped so a socket that dropped in the background has
	// the full grace period to reconnect after the user comes back.
	useEffect(() => {
		if (isConnected || !isPageVisible) {
			setShowDisconnected(false);
			return undefined;
		}

		const timeoutId = setTimeout(
			() => setShowDisconnected(true),
			DISCONNECTED_GRACE_MS
		);

		return () => clearTimeout(timeoutId);
	}, [isConnected, isPageVisible]);

	useEffect(() => {
		if (!showNewLayout) {
			return;
		}

		// // if dev mode is on, do not show the toast
		if (import.meta.env.DEV) {
			return;
		}

		if (showDisconnected) {
			toast.error(
				'Connection lost to server. Please check your internet connection.',
				{
					id: 'connection-lost',
					action: {
						label: 'Refresh',
						onClick: refresh,
					},
					duration: Infinity,
					position: toastPosition('top-right'),
				}
			);
			return;
		}

		toast.dismiss('connection-lost');
	}, [showDisconnected, showNewLayout, refresh]);

	//
	useEffect(() => {
		if (!socketIdentityKey) {
			return undefined;
		}

		// Get the token from the local storage
		const jwt = store.get('jwt');

		// Create the socket connection
		const webchatSocket = socketIOClient(
			`${getRealtimeApiUrl(window.location.href)}/webchat`,
			{
				transports: ['websocket'],
				query: {
					token: jwt,
					isEnterpriseEnabled,
					isTeamChatEnabled,
				},
			}
		);
		webchatSocketRef.current = webchatSocket;

		// Set up event listeners for connect and disconnect
		webchatSocket.on('connect', () => {
			const shouldCatchUp = wasDisconnectedRef.current;
			setWebchatSocketConnected(true);
			dispatch(setWebchatSocketId(webchatSocket.id));
			wasDisconnectedRef.current = false;

			if (shouldCatchUp) {
				runWebchatCatchUp({
					force: true,
					changedSince: disconnectedAtRef.current
						? new Date(disconnectedAtRef.current).toISOString()
						: undefined,
				});
			}

			disconnectedAtRef.current = null;
		});

		webchatSocket.on('disconnect', () => {
			wasDisconnectedRef.current = true;
			disconnectedAtRef.current = Date.now();
			setWebchatSocketConnected(false);
		});

		webchatSocket.on('fetch-current-voice-status', () => {
			dispatch(fetchCurrentVoiceStatus());
		});

		webchatSocket.on(
			'voice-forwarding-status',
			({ status, toUserName, toUserId } = {}) => {
				if (!status) return;
				if (toUserId || toUserName) {
					dispatch(
						setForwardingTargetUser({
							_id: toUserId || '',
							name: toUserName || 'teammate',
						}),
					);
				}
				dispatch(setForwardingStatus(status));
				if (
					status === 'answered' ||
					status === 'failed' ||
					status === 'canceled'
				) {
					dispatch(fetchCurrentVoiceStatus());
				}
			},
		);

		// if this user has multiple businesses, listen for messages sent to other locations
		if (businessCount > 1) {
			webchatSocket.on(
				'otherLocationMessage',
				(otherLocation) => {
					try {
						const currentBusinessGroup = businessGroupRef.current;

						// Only update if businessGroup has data
						if (
							!currentBusinessGroup ||
							currentBusinessGroup.length === 0
						) {
							return;
						}

						const updatedBusinessGroup = currentBusinessGroup.map(
							(biz) =>
								biz._id === otherLocation
									? { ...biz, hasUnreadMessages: true }
									: biz
						);

						dispatch(
							setBusinessGroup({
								businessGroup: updatedBusinessGroup,
							})
						);

						// play sound if the last message is sent by customer
						playNotificationSound(null, true);
					} catch (err) {
						console.log('error on otherLocationMessage:', err);
					}
				}
			);
		}

		if (isTeamChatEnabled) {
			webchatSocket.on(
				'newTeamConversation',
				(teamConversation) => {
					if (!teamConversation?._id) {
						return;
					}

					// A deleted group arrives as a tombstone on this channel
					// because the conversation no longer exists to be sent.
					if (teamConversation.isDeleted) {
						handleDeletedTeamConversationRef.current(
							teamConversation._id
						);
						return;
					}

					dispatch(addTeamConversation(teamConversation));
				}
			);

			webchatSocket.on(
				'newTeamMessage',
				({ teamMessage, teamConversation } = {}) => {
					dispatch(
						addMessageToConversation({
							teamMessage,
							teamConversation,
						})
					);

					dispatch(
						clearTypingIndicatorForMessage({
							_teamConversation: teamMessage?._teamConversation,
							message: teamMessage,
						})
					);
				}
			);

			webchatSocket.on(
				'updateTeamMessage',
				({ updatedTeamMessage } = {}) => {
					if (!updatedTeamMessage?._id) {
						return;
					}

					dispatch(updateTeamMessage({ updatedTeamMessage }));
				}
			);
		}

		webchatSocket.on('user_connected', (_user) => {
			dispatch(setUserOnline({ _user }));
		});

		webchatSocket.on('user_disconnected', (_user) => {
			dispatch(setUserOffline({ _user }));
		});

		webchatSocket.on('conversation_notes', (incomingNotes) => {
			try {
				dispatch(updateConversationsNotes(incomingNotes));
			} catch (err) {
				console.log('error updating conversations notes: ', err);
			}
		});

		webchatSocket.on('new_workflow_milestone_event', () => {
			try {
				// invalidate the workflow event cache
				dispatch(workflowEventUtil.invalidateTags(['WorkflowEvent']));

				// invalidate the project cache
				dispatch(projectUtil.invalidateTags(['Project']));

				// change the document title to the new milestone event
				document.title = 'New project update!';
			} catch (err) {
				console.log('error invalidating workflow events: ', err);
			}
		});

		// incoming folder
		/**
		 * {Object}
		 * properties:
		 * 	- _folder: {Mongoose OID} folder id
		 *  - _customerConversation: {Mongoose OID} customer conversation id
		 */
		webchatSocket.on('conversation_folder', (incomingFolder) => {
			try {
				dispatch(updateConversationFolder(incomingFolder));
			} catch (err) {
				console.log('error invalidating workflow events: ', err);
			}
		});

		webchatSocket.on('folder_updated', () => {
			try {
				dispatch(getAllFolders());
			} catch (err) {
				console.log('error retrieving folders: ', err);
			}
		});

		// Whenever a "conversation" message event is emitted from the node socket.io back-end, do the following:
		webchatSocket.on('conversation', (incomingConversation) => {
			try {
				const currentAuth = authRef.current;
				// Check if message data is valid
				// group conversations carry _customers instead of _customer
				const hasParticipants =
					incomingConversation?._customer ||
					(incomingConversation?.isGroup &&
						incomingConversation?._customers?.length);

				if (
					!incomingConversation ||
					typeof incomingConversation !== 'object' ||
					!incomingConversation._id ||
					!hasParticipants ||
					!incomingConversation.messages ||
					!incomingConversation._business ||
					(!currentAuth?.user?.isEnterpriseEnabled && // if not enterprise, check if the incoming conversation is for this business
						incomingConversation._business !==
							currentAuth?.user?._temporaryBusiness)
				) {
					return;
				}

				dispatch(
					addConversationToCustomerConversations(incomingConversation)
				);

				dispatch(
					clearTypingIndicatorForMessage({
						_customerConversation: incomingConversation._id,
						message: incomingConversation.messages.at(-1),
					})
				);

				if (
					hasAiEmployeeProduct(currentAuth) &&
					incomingConversation.handoffStatus === 'pending'
				) {
					dispatch(
						setAiEscalation({
							_customerConversation: incomingConversation._id,
						})
					);
				} else {
					// The hand-off is closed, for example by a teammate's reply. This
					// event is how the other screens hear of it, so the badge goes too.
					dispatch(clearEscalationIfClosed(incomingConversation));
				}

				// play sound if the last message is sent by customer
				playNotificationSound(incomingConversation);
			} catch (err) {
				console.log('Error handling incoming chat', err);
			}
		});

		// update the message with the new message
		webchatSocket.on('message', ({ message }) => {
			try {
				dispatch(addMessageToCustomerConversation(message));
			} catch (err) {
				console.log('Error handling incoming message', err);
			}
		});

		webchatSocket.on(
			'bulk_update_conversations',
			(_customerConversations) => {
				// update the customer conversations
				dispatch(getCustomerConversations({ _customerConversations }));
			}
		);

		if (aiEmployeeVisible) {
			webchatSocket.on('ai_status', (event) => {
				try {
					aiStatusListenerRef.current(event);
				} catch (err) {
					console.log('Error handling ai_status event:', err);
				}
			});

			webchatSocket.on('ai_draft', ({ _customerConversation, draftText, aiMeta, ...draft }) => {
				try {
					dispatch(setAiDraft({ _customerConversation, draftText, aiMeta, ...draft }));
				} catch (err) {
					console.log('Error handling ai_draft event:', err);
				}
			});

			webchatSocket.on('ai_escalation', ({ _customerConversation, reason, summary }) => {
				try {
					dispatch(setAiEscalation({ _customerConversation, reason, summary }));
					playNotificationSound(null, false);
					toast.warning(
						reason || 'AI escalated a conversation — human attention needed',
						{
							duration: 10000,
						}
					);
				} catch (err) {
					console.log('Error handling ai_escalation event:', err);
				}
			});
		}

		// listen for typing events
		webchatSocket.on('typing', (typingData) => {
			try {
				if (typingData.isTyping) {
					if (typingData.whoIsTyping !== authRef.current?.user?.name) {
						return dispatch(addTypingIndicator(typingData));
					}
					return;
				}

				return dispatch(removeTypingIndicator(typingData));
			} catch (err) {
				console.log('Error handling incoming typing notification', err);
			}
		});

		const refreshIntervalId = setInterval(() => {
			if (webchatSocket.connected) {
				webchatSocket.disconnect();
			}

			webchatSocket.connect();
		}, SOCKET_REFRESH_INTERVAL_MS);

		// Clean up the socket connection when the component unmounts
		return () => {
			clearInterval(refreshIntervalId);
			webchatSocket.disconnect();

			if (webchatSocketRef.current === webchatSocket) {
				webchatSocketRef.current = null;
			}
		};
	}, [
		aiEmployeeVisible,
		dispatch,
		isEnterpriseEnabled,
		isTeamChatEnabled,
		businessCount,
		playNotificationSound,
		runWebchatCatchUp,
		socketIdentityKey,
	]);

	useEffect(() => {
		let interval = null;

		if (!isOnline) {
			interval = setInterval(() => {
				setSeconds((oldSeconds) => oldSeconds + 1);
			}, 1000);
		} else if (isOnline && seconds !== 0) {
			clearInterval(interval);
		}

		if (isOnline && seconds > 3) {
			reset();
			runWebchatCatchUp({
				force: true,
				changedSince: new Date(
					Date.now() - seconds * 1000
				).toISOString(),
			});
			if (hasAiEmployeeProduct(authRef.current)) {
				dispatch(fetchPendingEscalations());
			}
		}

		return () => clearInterval(interval);
	}, [dispatch, isOnline, reset, runWebchatCatchUp, seconds]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			setIsPageVisible(!document.hidden);

			if (document.hidden) {
				hiddenAtRef.current = Date.now();
				return;
			}

			if (
				hiddenAtRef.current &&
				Date.now() - hiddenAtRef.current >= catchUpThrottleMs
			) {
				runWebchatCatchUp({
					changedSince: new Date(hiddenAtRef.current).toISOString(),
				});
			}

			hiddenAtRef.current = null;
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener(
				'visibilitychange',
				handleVisibilityChange
			);
		};
	}, [runWebchatCatchUp]);

	if (showNewLayout) {
		return null;
	}

	if (showDisconnected) {
		return (
			<IconButton
				aria-label="no connection"
				toolTipTitle="Connection lost to server. Click to refresh"
				onClick={refresh}
			>
				<WifiOff className="size-4" />
			</IconButton>
		);
	}

	// need to add this back
	// <DocumentTitleForIncomingMessage
	// 		activeTab={this.state.active}
	// 		incomingConversationTitle={this.state.incomingConversationTitle}
	// 		incomingMessageOccurred={this.state.incomingMessageOccurred}
	// 		otherLocationMessageOccurred={
	// 			this.state.otherLocationIncomingMessage
	// 		}
	// 		resetIncomingMessageOccurred={this.resetIncomingMessageOccurred}
	// 	/>

	return null;
};

export default ConnectionChecker;
